import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import ProjectCard from "@/components/ProjectCard";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { id: "all", label: "ALL PROJECTS" },
  { id: "restoration", label: "CUSTOM BUILDS" },
  { id: "coatings", label: "COATINGS" },
  { id: "3d_design", label: "3D DESIGN" },
];

export default function GalleryPage() {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // Seed projects first
        await axios.post(`${API}/seed-projects`);
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data);
        setFilteredProjects(response.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    if (activeCategory === "all") {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(p => p.category === activeCategory));
    }
  }, [activeCategory, projects]);

  return (
    <div className="min-h-screen pt-20" data-testid="gallery-page">
      {/* Header */}
      <section className="py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="font-mono text-xs tracking-[0.3em] text-amber-500 uppercase mb-4 block">
              OUR WORK
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl text-white tracking-tighter mb-6">
              PROJECT<br />
              <span className="text-amber-500">GALLERY</span>
            </h1>
            <p className="font-body text-lg text-zinc-300 leading-relaxed">
              Browse our collection of completed builds and custom projects. Each one tells a story of passion, precision, and dedication to the craft.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="bg-[#09090b] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-8 overflow-x-auto py-4">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`font-mono text-sm tracking-wide whitespace-nowrap transition-colors ${
                  activeCategory === cat.id
                    ? "text-amber-500 border-b-2 border-amber-500 pb-2"
                    : "text-zinc-400 hover:text-white pb-2 border-b-2 border-transparent"
                }`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 bg-[#09090b]" data-testid="projects-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-[4/3] bg-zinc-900 animate-pulse" />
              ))}
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-24">
              <p className="font-body text-zinc-400 text-lg">No projects found in this category.</p>
            </div>
          ) : (
            <motion.div 
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
              layout
            >
              {filteredProjects.map((project, index) => (
                <ProjectCard key={project.id} project={project} index={index} />
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
